import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Plus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/features/fraud/shared'
import { ScheduleEditor } from '@/features/promotions/schedule-editor'
import { discountErrorMessage } from '@/lib/api/discount-client'
import { useDiscountSchedules, useDiscountScope } from '@/lib/api/discount-queries'
import type { DiscountSchedule } from '@/lib/api/discount-types'
import { useEvent } from '@/lib/api/queries'
import type { Event } from '@/lib/api/types'
import { DEFAULT_EVENT_TIMEZONE, timeZoneLabel } from '@/lib/eventTime'
import { usePermission } from '@/lib/permissions'
import { cn } from '@/lib/utils'
import { Notice } from './editor-fields'
import { plural, scheduleStatus } from './list-format'
import { ScheduleListHeader, ScheduleRow } from './list-row'

// Statuses are worked out in the browser against the clock, so re-read it
// often enough that "Running now" flips without a reload.
const CLOCK_TICK_MS = 30_000

function useNow(intervalMs: number): Date {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), intervalMs)
    return () => window.clearInterval(id)
  }, [intervalMs])
  return now
}


function Frame({ children, busy }: { children: ReactNode; busy?: boolean }) {
  return (
    <div
      aria-busy={busy || undefined}
      className={cn(
        'rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 transition-opacity',
        busy && 'opacity-60',
      )}
    >
      {children}
    </div>
  )
}

function Loading() {
  return <div className="flex items-center justify-center py-16"><Spinner /></div>
}

function EmptyState({ title, body, action }: { title: string; body?: ReactNode; action?: ReactNode }) {
  return (
    <div className="px-6 py-12 text-center">
      <p className="text-sm font-medium text-neutral-900 dark:text-white">{title}</p>
      {body && <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">{body}</p>}
      {action && <div className="mt-4 flex flex-wrap justify-center gap-2">{action}</div>}
    </div>
  )
}

function NoSchedulesYet({ newButton }: { newButton: ReactNode }) {
  return (
    <EmptyState
      title="No promotions yet"
      body="A promotion discounts menu items during the times you choose: a one-off window or a recurring happy hour."
      action={newButton}
    />
  )
}

/**
 * Nothing active to show. Asks for the archived list too (only now, and it
 * primes the toggle) to tell "none yet" from "all archived".
 */
function NoActiveSchedules({ eventId, newButton, onShowArchived }: {
  eventId: number
  newButton: ReactNode
  onShowArchived: () => void
}) {
  const { data, isLoading, error } = useDiscountSchedules(eventId, { includeArchived: true })
  if (isLoading) return <Loading />
  if (error && !data) {
    return (
      <EmptyState
        title="No active promotions"
        body={<span className="text-red-600 dark:text-red-400">Could not check for archived promotions: {discountErrorMessage(error)}</span>}
        action={newButton}
      />
    )
  }
  const archived = data?.filter((schedule) => schedule.archived_at).length ?? 0
  if (!archived) return <NoSchedulesYet newButton={newButton} />
  return (
    <EmptyState
      title="No active promotions"
      body={`${plural(archived, 'archived promotion')} ${archived === 1 ? 'is' : 'are'} hidden.`}
      action={(
        <>
          <Button variant="outline" size="sm" onClick={onShowArchived}>Show archived</Button>
          {newButton}
        </>
      )}
    />
  )
}

function sortSchedules(schedules: DiscountSchedule[]): DiscountSchedule[] {
  // Newest first, archived at the bottom. Deliberately not by status: statuses
  // move with the clock and rows would reshuffle under the user.
  return [...schedules].sort((a, b) => Number(!!a.archived_at) - Number(!!b.archived_at) || b.id - a.id)
}

export function SchedulesTab({ eventId }: { eventId: number }) {
  const [includeArchived, setIncludeArchived] = useState(false)
  const { data: event, error: eventError } = useEvent(eventId)
  // Started here, next to the event, so they load side by side.
  const schedules = useDiscountSchedules(eventId, { includeArchived })
  const scope = useDiscountScope(eventId)

  if (eventError) {
    return (
      <Frame>
        <p className="p-6 text-sm text-red-600 dark:text-red-400">Failed to load the event: {discountErrorMessage(eventError)}</p>
      </Frame>
    )
  }
  if (!event) {
    return <Frame><Loading /></Frame>
  }
  if (scope.data && !scope.data.can_view) {
    return (
      <Frame>
        <EmptyState title="No promotions to show" body="You can't view promotions for this event's vendors or locations." />
      </Frame>
    )
  }
  return (
    <SchedulesList
      eventId={eventId}
      event={event}
      schedules={schedules}
      scope={scope}
      includeArchived={includeArchived}
      onIncludeArchivedChange={setIncludeArchived}
    />
  )
}

function SchedulesList({ eventId, event, schedules, scope, includeArchived, onIncludeArchivedChange }: {
  eventId: number
  event: Event
  schedules: ReturnType<typeof useDiscountSchedules>
  scope: ReturnType<typeof useDiscountScope>
  includeArchived: boolean
  onIncludeArchivedChange: (value: boolean) => void
}) {
  // The permission is a first filter; the backend has the last word: the
  // scope says whether the viewer may create (hidden until it has loaded),
  // and each schedule's `can_edit` whether they may change it.
  const canEditDiscounts = usePermission('can_edit_discounts')
  const mayCreate = canEditDiscounts && !!scope.data?.can_edit
  const canManage = (schedule: DiscountSchedule) => canEditDiscounts && !!schedule.can_edit

  const now = useNow(CLOCK_TICK_MS)
  const [editor, setEditor] = useState<{ id: number | null; name?: string } | null>(null)
  // "X is saved." / "X is archived."; `focus` when the row that had focus is gone.
  const [notice, setNotice] = useState<{ text: ReactNode; focus: boolean } | null>(null)
  const noticeRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (notice?.focus) noticeRef.current?.focus()
  }, [notice])

  const timeZone = event.timezone || DEFAULT_EVENT_TIMEZONE
  const zoneLabel = useMemo(() => timeZoneLabel(timeZone), [timeZone])
  const { data, isLoading, error, isPlaceholderData } = schedules
  const rows = useMemo(() => sortSchedules(data ?? []), [data])
  const archivedCount = rows.filter((schedule) => schedule.archived_at).length
  const runningCount = rows.filter((schedule) => scheduleStatus(schedule, timeZone, now, event).status === 'running').length
  const manageable = rows.some(canManage)

  const openNew = () => {
    setNotice(null)
    setEditor({ id: null })
  }
  const openSchedule = (schedule: DiscountSchedule) => {
    setNotice(null)
    setEditor({ id: schedule.id, name: schedule.name })
  }
  const named = (schedule: DiscountSchedule) => (
    <span className="font-medium">{schedule.name || `Promotion #${schedule.id}`}</span>
  )
  const newButton = mayCreate ? (
    <Button size="sm" onClick={openNew}>
      <Plus className="mr-1.5 h-4 w-4" />
      New promotion
    </Button>
  ) : null

  let summary: string | null = null
  if (rows.length) {
    const active = rows.length - archivedCount
    summary = plural(active, includeArchived ? 'active promotion' : 'promotion')
    // Placeholder rows are the other list's, still on screen while this one loads.
    if (includeArchived && !isPlaceholderData) summary += `, ${archivedCount} archived`
    if (runningCount) summary += ` · ${runningCount} running now`
  }

  // The empty state carries its own New button.
  const showsEmptyState = !error && !isLoading && rows.length === 0

  let body: ReactNode
  if (error) {
    body = <p className="p-6 text-sm text-red-600 dark:text-red-400">Failed to load promotions: {discountErrorMessage(error)}</p>
  } else if (isLoading) {
    body = <Loading />
  } else if (showsEmptyState) {
    body = includeArchived
      ? <NoSchedulesYet newButton={newButton} />
      : <NoActiveSchedules eventId={eventId} newButton={newButton} onShowArchived={() => onIncludeArchivedChange(true)} />
  } else {
    body = (
      <>
        <ScheduleListHeader manageable={manageable} timeZoneLabel={zoneLabel} />
        <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">
          {rows.map((schedule) => (
            <ScheduleRow
              key={schedule.id}
              eventId={eventId}
              schedule={schedule}
              event={event}
              timeZone={timeZone}
              now={now}
              manageable={manageable}
              canManage={canManage(schedule)}
              promotionsOff={!event.enable_discount_schedules}
              onOpen={openSchedule}
              onArchived={(archived) => setNotice({ text: <>{named(archived)} is archived.</>, focus: true })}
            />
          ))}
        </ul>
      </>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          {summary && <p className="text-sm font-medium text-neutral-900 dark:text-white">{summary}</p>}
          <label className="flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-300">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => onIncludeArchivedChange(e.target.checked)}
            />
            Include archived
          </label>
        </div>
        {!showsEmptyState && newButton}
      </div>
      <p className="-mt-2 text-xs text-neutral-500 dark:text-neutral-400">
        Times are in the event's timezone ({zoneLabel}).
      </p>
      {canEditDiscounts && scope.error && (
        <p className="-mt-2 text-xs text-red-600 dark:text-red-400">
          Could not check what you can change here, so New promotion is hidden: {discountErrorMessage(scope.error)}
        </p>
      )}

      {notice && (
        <div ref={noticeRef} tabIndex={-1} className="rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
          <Notice kind="success">
            <div className="flex items-start justify-between gap-3">
              <span>{notice.text}</span>
              <button
                type="button"
                onClick={() => setNotice(null)}
                aria-label="Dismiss"
                className="shrink-0 rounded p-0.5 hover:bg-green-100 dark:hover:bg-green-900/40"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </Notice>
        </div>
      )}

      <Frame busy={isPlaceholderData}>{body}</Frame>

      {editor && (
        <ScheduleEditor
          eventId={eventId}
          scheduleId={editor.id}
          name={editor.name}
          onClose={() => setEditor(null)}
          onSaved={(saved) => {
            setEditor(null)
            // Focus goes back to Edit or New, except from the empty state, whose New button is about to go.
            setNotice({ text: <>{named(saved)} is saved.</>, focus: showsEmptyState })
          }}
        />
      )}
    </div>
  )
}
